<template>
  <div id="app">
    <transition name="page" mode="out-in">
      <component :is="layout" v-if="layout" />
    </transition>
  </div>
</template>

<script>
import auth from '@groovepages/gd-lib/auth';
import Loader from 'vue-loading-overlay';
import store from '@/state';

// Load layout components dynamically.
const requireContext = require.context('@/layouts', false, /.*\.vue$/);

const layouts = requireContext
  .keys()
  .map((file) => [file.replace(/(^.\/)|(\.vue$)/g, ''), requireContext(file)])
  .reduce((components, [name, component]) => {
    components[name] = component.default || component;
    return components;
  }, {});

export default {
  components: {
    Loader
  },

  data: () => ({
    layout: null,
    defaultLayout: 'default',
  }),

  /**
   * Used to return app info.
   * @return {object} returns details related to app.
   */
  metaInfo() {
    const appName = this.supBrandName;

    return {
      title: appName,
      titleTemplate: `%s · ${appName}`,
    };
  },

   // Used to commit 'login' mutation if user is authenticated.
  created() {
    if (auth.authenticatedUser()) {
      store.commit('login', auth.authenticatedUser());
    }
  },

  // Used to set reference of Loading component.
  mounted() {
    this.$loading = this.$refs.loading;
  },

  computed: {

    // Used to return brandData value from store.
    supBrandName() {
      return this.$store.getters.supBrand.title;
    },
  },


  methods: {
    /**
     * Set the application layout.
     *
     * @param {String} layout
     */
    setLayout(layout) {
      if (!layout || !layouts[layout]) {
        layout = this.defaultLayout;
      }

      this.layout = layouts[layout];
    },
  },
};
</script>

<style>
@import url('https://stackpath.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css');
</style>

<style lang="scss">
.dropdown-toggle {
  background-image: none !important;
  border: 1px solid dodgerblue !important;
  border-radius: 30px;
}

.dropdown-toggle:hover {
  background: transparent;
}

.dropdown-button-container {
  display: flex;
  align-items: center;

  .btnx {
    border-radius: 5px 0px 0px 5px;
  }

  .btn-drop {
    border-radius: 0px 5px 5px 0px;
    border-left: 1px solid rgba(255, 255, 255, 0.2);
  }
}

.groovemail-common-paginate {
  .VuePagination__pagination.pagination.VuePagination__pagination {
    display: flex !important;
    margin-top: 30px;
    .VuePagination__pagination-item {
      float: left;
      font-size: 14px;
      text-align: center;
      color: #191d3a;
      padding: 4px 8px;
      font-weight: 600;
      margin-left: 4px;
      line-height: 1.25;
      cursor: pointer;
    }

    .VuePagination__pagination-item.disabled {
      color: #8f92ab;
      cursor: not-allowed;
    }

    .VuePagination__pagination-item.page-item.active {
      border-radius: 6px !important;
    }
  }
  .VuePagination__count.VuePagination__count {
    display: none;
  }
}

#generic-email-builder-modal,
#show-emailpreview-modal,
#campaign-message-wrapper {
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p {
    line-height: 1.42;
    font-weight: normal;
    margin: 0;
  }
  h1 {
    font-size: 2em !important;
    font-weight: bold;
  }
  h2 {
    font-size: 1.5em !important;
    font-weight: bold;
  }
  h3 {
    font-size: 1.17em !important;
    font-weight: bold;
  }
  h4 {
    font-size: 1em !important;
    font-weight: bold;
  }
  h5 {
    font-size: 0.83em !important;
    font-weight: bold;
  }
  h6 {
    font-size: 0.67em !important;
    font-weight: bold;
  }
  p {
    font-size: 14px !important;
  }
}

#generic-email-builder-modal,
#show-emailpreview-modal,
#campaign-message-wrapper {
  .gm-beefree-preview-content {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      font-size: unset !important;
      font-weight: unset !important;
    }

    .border {
      border-width: 0px;
    }
  }
}

.gm-befree-email-templates-modal > .max-h-full.w-full.h-full.p-8.flex.items-center.justify-center {
  padding: 0px !important;
}

.gm-befree-email-templates-modal >
  .border-solid.border-b.border-grey-200.h-16.w-full.flex.flex-row.items-center.justify-between.px-8.text-grey-700
  {
    display: none;
  }

#configure-dynamic-variables-modal {
  & > .max-h-full > .bg-white.rounded-lg.shadow-2xl {
    width: 400px !important;
  }
}

#configure-button-modal {
  & > .max-h-full > .bg-white.rounded-lg.shadow-2xl {
    width: 500px !important;
  }
}

.generic-email-builder {
  .ql-editor {
    outline: none;
    overflow: visible;
    padding: 5px 5px 5px 5px;
    outline: none;
  }
  .ql-bubble .ql-tooltip {
    z-index: 9;
  }
  .ql-bubble .ql-toolbar {
    width: 600px;
    background: #292c49;
    -webkit-box-shadow: 0px 3px 2px rgba(0, 0, 0, 0.2);
    box-shadow: 0px 3px 2px rgba(0, 0, 0, 0.2);
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    border-radius: 50px;
    button {
      width: auto !important;
      padding: 3px;
    }
    .ql-tooltip .ql-tooltip-arrow {
      border-bottom-color: #292c49;
    }
  }
  .ql-blank::before {
    font-size: 14px;
    font-style: normal;
  }
}

.swal2-popup.swal2-modal.swal2-show {
  max-height: 90%;
  padding: 16px;
  .swal2-content {
    overflow: auto;
  }
}

// custom button with Plus icon
.plus-round-icon {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  &:before,
  &:after {
    content: '';
    position: absolute;
    background: var(--brand-primary-invert);
  }
  &:before {
    left: 50%;
    top: 6px; /* this defines how much black "border" there should be */
    bottom: 6px;
    width: 2px;
    transform: translateX(-50%);
  }
  &:after {
    top: 50%;
    left: 6px;
    right: 6px;
    height: 2px;
    transform: translateY(-50%);
  }
}

// list chip 
.list-chip {
  border-radius: 6px;
  line-height: 31px;
}

// 
.bg-black-transparent {
  position: relative;
  z-index: 0;
  &:after {
    content: '';
    position: absolute;
    background-color: transparent;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    opacity: 0.4;
    z-index: -1;
    transition: all 0.2s ease-in-out;
  }

  &:hover:after {
    background-color: var(--brand-secondary-lightest);
    opacity: 0.4;
  }
}

// custom select box style
.custom-selectbox {
  height: 40px !important;
  .border-solid.cursor-pointer {
    font-size: 14px;
    height: 40px;
    background-color: #fff;
    border-right: 1px solid;
    border-top: 1px solid;
    border-bottom: 1px solid;
    border-color: #d5d8e5;
    border-radius: 6px;
    .select-none {
      padding: 0 10px 0 16px;
      color: #191d3a;
    }
  }
  svg {
    color: #d5d8e5;
    height: 20px;
    top: 2px;
    pointer-events: none;
    margin-right: 16px;
  }
  .italic.select-none {
    display: none;
  }

  .custom-scrollbars {
    max-height: 300px !important;
    overflow: auto;
    border-radius: 6px;
    box-shadow: 20px 20px 40px 0 rgba(22, 29, 37, 0.05);
    .rounded-lg.select-none {
      width: 100%;
      background: #fff;
      border-top: 1px solid #d5d8e5;
      text-align: center;
      font-weight: bold;
      padding: 21px 0;
      text-transform: capitalize;
      cursor: pointer;
      position: sticky;
      bottom: 0;
      left: 0;
      &:before {
        content: '+ ';
      }
    }
    .select-none {
      font-size: 14px;
      cursor: pointer;
      color: #191d3a;
      padding: 12px 24px;
      &:hover {
        background: #fff;
        color: inherit;
      }
    }
  }
}

// custom style for sequence menu
.item .node {
  height: 35px !important;
  width: auto;
  border: 0 !important;
  border-radius: 6px !important;
  background-color: var(--brand-secondary-light) !important;
  border-color: var(--brand-secondary-light) !important;
  margin: 8px 16px;
  padding-bottom: 5px !important;
  
  .node-title, .title {
    color: var(--brand-secondary-invert) !important;
  }
}

// custom style for dataPicker
.daterangepicker {
  tbody td.in-range {
    background-color: var(--brand-primary-lightest);
  }
  
  div.ranges li.active,
  tbody td.active, tbody td.active:hover {
    background-color: var(--brand-primary);
    color: var(--brand-primary-invert);
  }
}


// custom style for alert librray
.swal2-popup.swal2-modal.swal2-show {
  .swal2-content svg {
    stroke: var(--brand-primary);
  }

  button.swal2-confirm {
    background-color: var(--brand-primary) !important;
    color: var(--brand-primary-invert);
    &:hover {
      background-color: var(--brand-primary-dark);
    }
    &:focus {
      box-shadow: none;
    }
  }
}

/* custom style for treeselect */
// shadow
.category-select-shadow .vue-treeselect__menu-container div.vue-treeselect__menu {
  box-shadow: 0px 0px 40px 0 rgba(22, 29, 37, 0.05);
}
// error
.error.vue-treeselect:not(.vue-treeselect--disabled):not(.vue-treeselect--focused) .vue-treeselect__control:hover,
.error.custom-treeselect .vue-treeselect__control,
.error div.vue-treeselect__control {
  border-color: var(--brand-danger);
}

// custom z-index class
.z-99 {
  z-index: 99;
}

// custom style for tabs
.gs-default-tabs-active-line {
  margin-bottom: -2px;
}
</style>
